import { FC, useCallback, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  Alert,
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { OpenInNew } from "@mui/icons-material"
import {
  GridColDef,
  GridRowId,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro"
import { DataGrid, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { formatDateAndTime, getApiErrorMessage } from "@/core/utils"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks/store"
import { GuestBot } from "../../interfaces"
import { useChatsListQuery } from "../../store"
import {
  clearSelection,
  selectSelectedChatIds,
  setPageSelection,
} from "../../store/chatsSelectionSlice"
import { useGuestBotWriteAccess } from "../../hooks"
import { MAILING_CHATS_CAP, isAtCap } from "../../utils/mailingSelection"
import { JoinWizard } from "./joinWizard"
import { MailingWizard } from "./mailingWizard"
import type { Notify } from "./notify"

// Пороги как в основном UI chat-analyze (FraudScore.tsx): <20 ок, <50 вопросы, >=50 риск.
// §3: индикатор, не вердикт — действия оператора по скору не блокируются.
function fraudColor(
  score: number,
): "success.main" | "warning.main" | "error.main" {
  if (score < 20) return "success.main"
  if (score < 50) return "warning.main"
  return "error.main"
}

export const PoolTable: FC = () => {
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlSearch = searchParams.get("search") || undefined
  const dispatch = useAppDispatch()
  const selected = useAppSelector(selectSelectedChatIds)
  const [searchInput, setSearchInput] = useState(urlSearch ?? "")
  const [search, setSearch] = useState<string | undefined>(urlSearch)
  const [joinWizardOpen, setJoinWizardOpen] = useState(false)
  const [mailingWizardOpen, setMailingWizardOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)

  const { data, isFetching, isError, error, refetch } = useChatsListQuery({
    queryParams: {
      role: "guest-bot",
      limit: pageSize,
      offset: page * pageSize,
      ...(search ? { search } : {}),
    },
  })

  // Стабильные колбэки: JoinWizard/MailingWizard обёрнуты в React.memo.
  const notify: Notify = useCallback(
    (variant, message) => setSnackbar({ variant, message }),
    [],
  )
  const handleJoinWizardClose = useCallback(() => setJoinWizardOpen(false), [])
  const handleMailingWizardClose = useCallback(
    () => setMailingWizardOpen(false),
    [],
  )
  const handleOpenJob = useCallback(
    (jobId: string) =>
      navigate(`/telegram/guest-bots/chats/join-jobs/${jobId}`),
    [navigate],
  )
  // D-15: 201 → снять выделение; навигация к прогрессу — внутри визарда (§5.4).
  const handleCreated = useCallback(() => {
    dispatch(clearSelection())
    setMailingWizardOpen(false)
  }, [dispatch])

  const commitSearch = () => {
    const nextSearch = searchInput.trim() || undefined
    if (nextSearch === search) return
    setSearch(nextSearch)
    // Новый поиск = новая выборка: offset старой страницы дал бы ложное
    // «Ничего не найдено» — сброс на первую страницу (useUrlPagination
    // опускает дефолты из URL).
    // Объединяем сброс page и установку search в один вызов setSearchParams,
    // так как react-router useSearchParams берет snapshot searchParams
    // текущего рендера.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (nextSearch) next.set("search", nextSearch)
        else next.delete("search")
        next.delete("page")
        return next
      },
      { replace: true },
    )
  }

  // sortable: false — API /chats сортировку не поддерживает (порядок серверный
  // joinedAt DESC); клиентская сортировка страницы вводила бы в заблуждение.
  const columns: GridColDef<GuestBot.PoolChat>[] = [
    {
      field: "title",
      headerName: "Чат",
      flex: 2,
      sortable: false,
      valueGetter: (params) => params.row.title ?? `Чат ${params.row.chatId}`,
    },
    {
      field: "type",
      headerName: "Тип",
      flex: 1,
      sortable: false,
      valueGetter: (params) => params.row.type ?? "—",
    },
    {
      field: "joinedAt",
      headerName: "Вступили",
      flex: 1.5,
      sortable: false,
      valueGetter: (params) => formatDateAndTime(params.row.joinedAt),
    },
    {
      field: "fraudScore",
      headerName: "Скор",
      flex: 1,
      sortable: false,
      renderCell: (params) =>
        params.row.fraudScore === null ? (
          "—"
        ) : (
          <Tooltip
            title={
              params.row.analyzedAt
                ? `${formatDateAndTime(params.row.analyzedAt)} — последний анализ (любой источник)`
                : "Последний анализ (любой источник)"
            }
          >
            <Typography
              variant="body2"
              component="span"
              fontWeight={600}
              color={fraudColor(params.row.fraudScore)}
            >
              {params.row.fraudScore.toLocaleString("ru-RU", {
                maximumFractionDigits: 1,
              })}
            </Typography>
          </Tooltip>
        ),
    },
    {
      field: "joinJobId",
      headerName: "Источник",
      flex: 0.7,
      sortable: false,
      // joinJobId always present (delta §3.1, F5)
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label="Открыть вступление"
          onClick={() =>
            navigate(
              `/telegram/guest-bots/chats/join-jobs/${params.row.joinJobId}`,
            )
          }
        >
          <OpenInNew fontSize="small" />
        </IconButton>
      ),
    },
    { field: "role", headerName: "Роль", flex: 1, sortable: false },
  ]

  const rows = useMemo(() => data?.items ?? [], [data?.items])
  const total = data?.total ?? 0
  const pageIds = useMemo(() => rows.map((row) => row.chatId), [rows])
  // Контролируемая модель: пересечение стор-выбора и id текущей страницы.
  const rowSelectionModel: GridRowSelectionModel = useMemo(() => {
    const selectedSet = new Set(selected)
    return pageIds.filter((id) => selectedSet.has(id))
  }, [pageIds, selected])
  const handleSelectionChange = (model: GridRowSelectionModel) => {
    dispatch(setPageSelection({ pageIds, selectedIds: model.map(Number) }))
  }
  // На капе блокируются только НЕвыбранные строки — выбранные можно снять.
  const isRowSelectable = (params: { id: GridRowId }) =>
    !isAtCap(selected) || selected.includes(Number(params.id))

  return (
    <>
      <Box
        mb={1}
        display="flex"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <TextField
          size="small"
          label="Поиск по названию"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onBlur={commitSearch}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitSearch()
          }}
          sx={{ minWidth: 240 }}
        />
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <Typography
            variant="body2"
            color={isAtCap(selected) ? "error" : undefined}
          >
            {`Выбрано: ${selected.length} / ${MAILING_CHATS_CAP}`}
          </Typography>
          <Button size="small" onClick={() => dispatch(clearSelection())}>
            Снять все
          </Button>
          {canWrite && (
            <>
              <Button
                variant="outlined"
                disabled={selected.length === 0}
                onClick={() => setMailingWizardOpen(true)}
              >
                {`Создать рассылку (${selected.length})`}
              </Button>
              <Button
                variant="contained"
                onClick={() => setJoinWizardOpen(true)}
              >
                Вступить в группы
              </Button>
            </>
          )}
        </Box>
      </Box>
      {isAtCap(selected) && (
        <Typography variant="caption" color="error" display="block" mb={1}>
          Максимум 1000 чатов на рассылку
        </Typography>
      )}
      {isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Повторить
            </Button>
          }
        >
          {getApiErrorMessage(error)}
        </Alert>
      ) : !isFetching && search && total === 0 ? (
        // Закоммиченный поиск без совпадений ≠ пустой пул: без CTA.
        <Typography>Ничего не найдено.</Typography>
      ) : !isFetching && total === 0 ? (
        <Typography>
          {`Список пуст.${
            canWrite
              ? " Нажмите „Вступить в группы“, чтобы добавить первые чаты."
              : ""
          }`}
        </Typography>
      ) : (
        <Box height="70vh">
          <DataGrid<GuestBot.PoolChat>
            columns={columns}
            rows={rows}
            getRowId={(row) => row.chatId}
            loading={isFetching}
            checkboxSelection
            isRowSelectable={isRowSelectable}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleSelectionChange}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
          />
        </Box>
      )}

      {joinWizardOpen && (
        <JoinWizard
          notify={notify}
          onClose={handleJoinWizardClose}
          onOpenJob={handleOpenJob}
        />
      )}
      {mailingWizardOpen && (
        <MailingWizard
          chatIds={selected}
          notify={notify}
          onClose={handleMailingWizardClose}
          onCreated={handleCreated}
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </>
  )
}
